import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { Navigation } from "@/components/sections/Navigation";
import { Footer } from "@/components/sections/Footer";
import { EventBreadcrumb } from "@/components/ui/breadcrumb-nav";
import { SidebarAd } from "@/components/ads/SidebarAd";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Calendar, MapPin, Clock, ExternalLink, ArrowLeft, ArrowRight, Share2, Heart, AlertCircle, AlertTriangle, ChevronLeft, ChevronRight, MessageCircle, Send, Briefcase, GraduationCap } from "lucide-react";
import { motion } from "framer-motion";
import { useFavoriteEvents } from "@/hooks/useFavoriteEvents";
import { useAuth } from "@/hooks/useAuth";
interface EventData {
  id: string;
  title: string;
  title_ar: string | null;
  description: string | null;
  description_ar: string | null;
  date: string | null;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  location_ar: string | null;
  image_url: string | null;
  category: string | null;
  external_link: string | null;
  source_name: string;
  ticket_status: string | null;
}
interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isPast: boolean;
}
const EventDetails = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const {
    i18n
  } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";
  const [event, setEvent] = useState<EventData | null>(null);
  const [similarEvents, setSimilarEvents] = useState<EventData[]>([]);
  const [countdown, setCountdown] = useState<CountdownTime | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dominantColor, setDominantColor] = useState<string>("#f3f4f6");
  const {
    toggleFavorite,
    isFavorite
  } = useFavoriteEvents();
  const {
    user
  } = useAuth();

  // Extract dominant color from image
  const extractDominantColor = (imageUrl: string) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      
      canvas.width = 50;
      canvas.height = 50;
      ctx.drawImage(img, 0, 0, 50, 50);
      
      try {
        const imageData = ctx.getImageData(0, 0, 50, 50).data;
        let r = 0, g = 0, b = 0, count = 0;
        
        for (let i = 0; i < imageData.length; i += 4) {
          r += imageData[i];
          g += imageData[i + 1];
          b += imageData[i + 2];
          count++;
        }
        
        r = Math.round(r / count);
        g = Math.round(g / count);
        b = Math.round(b / count);
        
        setDominantColor(`rgb(${r}, ${g}, ${b})`);
      } catch (e) {
        console.log("Could not extract color from image");
      }
    };
    img.src = imageUrl;
  };

  useEffect(() => {
    if (event?.image_url) {
      extractDominantColor(event.image_url);
    }
  }, [event?.image_url]);

  // Calculate countdown
  const calculateCountdown = (dateStr: string): CountdownTime => {
    const eventDate = new Date(dateStr);
    const now = new Date();
    const diff = eventDate.getTime() - now.getTime();
    if (diff <= 0) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        isPast: true
      };
    }
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff % (1000 * 60 * 60 * 24) / (1000 * 60 * 60));
    const minutes = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));
    const seconds = Math.floor(diff % (1000 * 60) / 1000);
    return {
      days,
      hours,
      minutes,
      seconds,
      isPast: false
    };
  };

  // Update countdown every second
  useEffect(() => {
    if (!event?.date) return;
    const updateCountdown = () => {
      setCountdown(calculateCountdown(event.date!));
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [event?.date]);
  useEffect(() => {
    const fetchEvent = async () => {
      if (!id) {
        setError("Event ID not found");
        setLoading(false);
        return;
      }
      try {
        const {
          data,
          error: fetchError
        } = await supabase.from("scraped_events").select("*").eq("id", id).single();
        if (fetchError) throw fetchError;
        if (data) {
          setEvent(data);
          // Update OG meta tags dynamically
          updateMetaTags(data);

          // Fetch similar events from the same category
          if (data.category) {
            const {
              data: similar
            } = await supabase.from("scraped_events").select("*").eq("category", data.category).eq("is_published", true).neq("id", id).limit(4);
            if (similar) {
              setSimilarEvents(similar);
            }
          }
        } else {
          setError("Event not found");
        }
      } catch (err) {
        console.error("Error fetching event:", err);
        setError("Failed to load event");
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();

    // Cleanup meta tags on unmount
    return () => {
      resetMetaTags();
    };
  }, [id]);
  const updateMetaTags = (eventData: EventData) => {
    const title = isRTL ? eventData.title_ar || eventData.title : eventData.title;
    const description = isRTL ? eventData.description_ar || eventData.description || "" : eventData.description || "";
    const imageUrl = eventData.image_url || "https://visitevent.live/og-image.png";
    const pageUrl = `https://visitevent.live/event/${eventData.id}`;

    // Update document title
    document.title = `${title} | Visit Event`;

    // Helper function to update or create meta tag
    const setMetaTag = (property: string, content: string, isName = false) => {
      const attr = isName ? "name" : "property";
      let meta = document.querySelector(`meta[${attr}="${property}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attr, property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Open Graph tags
    setMetaTag("og:title", title);
    setMetaTag("og:description", description.substring(0, 160));
    setMetaTag("og:image", imageUrl);
    setMetaTag("og:url", pageUrl);
    setMetaTag("og:type", "website");
    setMetaTag("og:site_name", "Visit Event");

    // Twitter Card tags
    setMetaTag("twitter:card", "summary_large_image", true);
    setMetaTag("twitter:title", title, true);
    setMetaTag("twitter:description", description.substring(0, 160), true);
    setMetaTag("twitter:image", imageUrl, true);

    // General meta description
    setMetaTag("description", description.substring(0, 160), true);
  };
  const resetMetaTags = () => {
    document.title = "دليل يدل - Yadel";
  };
  const getShareUrl = () => `https://visitevent.live/event/${event?.id}`;
  const getShareTitle = () => isRTL ? event?.title_ar || event?.title : event?.title;
  const getShareText = () => isRTL ? event?.description_ar || event?.description : event?.description;
  const shareToWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(`${getShareTitle()}\n${getShareUrl()}`)}`;
    window.open(url, '_blank');
  };
  const shareToTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(getShareTitle() || '')}&url=${encodeURIComponent(getShareUrl())}`;
    window.open(url, '_blank');
  };
  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}`;
    window.open(url, '_blank');
  };
  const shareToTelegram = () => {
    const url = `https://t.me/share/url?url=${encodeURIComponent(getShareUrl())}&text=${encodeURIComponent(getShareTitle() || '')}`;
    window.open(url, '_blank');
  };
  const copyLink = async () => {
    await navigator.clipboard.writeText(getShareUrl());
  };
  const handleFavorite = () => {
    if (event && user) {
      toggleFavorite(event.id, isRTL);
    } else if (!user) {
      navigate("/auth");
    }
  };
  if (loading) {
    return <div className={`min-h-screen bg-gradient-to-b from-gray-50 to-white ${isRTL ? "rtl" : "ltr"}`} dir={isRTL ? "rtl" : "ltr"}>
        <Navigation isRTL={isRTL} setRegistrationType={() => {}} handleRegistrationClick={() => {}} />
        <div className="container mx-auto px-4 py-8">
          <Skeleton className="h-8 w-32 mb-6" />
          <div className="grid lg:grid-cols-2 gap-8">
            <Skeleton className="h-[400px] rounded-3xl" />
            <div className="space-y-4">
              <Skeleton className="h-10 w-3/4" />
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-12 w-40" />
            </div>
          </div>
        </div>
        <Footer />
      </div>;
  }
  if (error || !event) {
    return <div className={`min-h-screen bg-gradient-to-b from-gray-50 to-white ${isRTL ? "rtl" : "ltr"}`} dir={isRTL ? "rtl" : "ltr"}>
        <Navigation isRTL={isRTL} setRegistrationType={() => {}} handleRegistrationClick={() => {}} />
        <div className="container mx-auto px-4 py-20 text-center">
          <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            {isRTL ? "الفعالية غير موجودة" : "Event Not Found"}
          </h1>
          <p className="text-gray-600 mb-6">
            {isRTL ? "عذراً، لم نتمكن من العثور على هذه الفعالية" : "Sorry, we couldn't find this event"}
          </p>
          <Button onClick={() => navigate("/imported-events")} className="gap-2">
            {isRTL ? <ArrowRight className="w-4 h-4" /> : <ArrowLeft className="w-4 h-4" />}
            {isRTL ? "العودة للفعاليات" : "Back to Events"}
          </Button>
        </div>
        <Footer />
      </div>;
  }
  const eventTitle = isRTL ? event.title_ar || event.title : event.title;
  const eventDescription = isRTL ? event.description_ar || event.description : event.description;
  const rawLocation = isRTL ? event.location_ar || event.location : event.location;
  
  // Helper to check if location is a Google Maps URL or coordinates
  const isGoogleMapsUrl = (str: string | null): boolean => {
    if (!str) return false;
    const patterns = [
      /google\.com\/maps/i,
      /maps\.google/i,
      /goo\.gl\/maps/i,
      /!3d[\d.]+!4d[\d.]+/i,
      /^[0-9a-f]+:0x[0-9a-f]+/i,
      /@[\d.]+,[\d.]+/i,
      /entry=ttu/i,
      /g_ep=/i,
      /^[\d.]+,\s*[\d.]+$/,
    ];
    return patterns.some(pattern => pattern.test(str));
  };

  // Clean location or show placeholder
  const eventLocation = rawLocation && !isGoogleMapsUrl(rawLocation) ? rawLocation : null;
  const hasMapLink = rawLocation && isGoogleMapsUrl(rawLocation);
  const pageUrl = `https://visitevent.lovable.app/event/${event.id}`;
  const imageUrl = event.image_url || "https://visitevent.lovable.app/og-image.png";

  return <div className={`min-h-screen bg-gradient-to-b from-gray-50 to-white ${isRTL ? "rtl" : "ltr"}`} dir={isRTL ? "rtl" : "ltr"}>
      {/* Dynamic SEO Meta Tags */}
      <Helmet>
        <title>{eventTitle} | زُر فعالية</title>
        <meta name="description" content={eventDescription?.substring(0, 160) || "تفاصيل الفعالية"} />
        <meta name="keywords" content={`${eventTitle}, فعاليات, ${event.category || "أحداث"}, السعودية`} />
        <link rel="canonical" href={pageUrl} />
        
        {/* Open Graph */}
        <meta property="og:type" content="event" />
        <meta property="og:title" content={eventTitle} />
        <meta property="og:description" content={eventDescription?.substring(0, 160) || "تفاصيل الفعالية"} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:site_name" content="زُر فعالية" />
        <meta property="og:locale" content={isRTL ? "ar_SA" : "en_US"} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={eventTitle} />
        <meta name="twitter:description" content={eventDescription?.substring(0, 160) || "تفاصيل الفعالية"} />
        <meta name="twitter:image" content={imageUrl} />
        
        {/* Event Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Event",
            "name": eventTitle,
            "description": eventDescription,
            "image": imageUrl,
            "startDate": event.date,
            "location": {
              "@type": "Place",
              "name": eventLocation || "السعودية"
            },
            "url": pageUrl,
            "organizer": {
              "@type": "Organization",
              "name": event.source_name
            }
          })}
        </script>
      </Helmet>

      <Navigation isRTL={isRTL} setRegistrationType={() => {}} handleRegistrationClick={() => {}} />
      
      <main className="container mx-auto px-4 py-6 md:py-10">
        {/* Breadcrumb Navigation */}
        <EventBreadcrumb 
          eventTitle={eventTitle} 
          eventTitleAr={event.title_ar || undefined}
          category={event.category || undefined}
        />

        {/* Back Button */}
        <motion.div initial={{
        opacity: 0,
        x: isRTL ? 20 : -20
      }} animate={{
        opacity: 1,
        x: 0
      }} className="mb-6">
          <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2 text-gray-600 hover:text-primary">
            {isRTL ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
            {isRTL ? "رجوع" : "Back"}
          </Button>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Image Section */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="relative">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3]" style={{
            background: dominantColor,
            boxShadow: '10px 10px 30px #d1d1d1, -10px -10px 30px #ffffff'
          }}>
              {event.image_url ? <img src={event.image_url} alt={eventTitle} className="w-full h-full object-contain" /> : <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <Calendar className="w-20 h-20 text-gray-300" />
                </div>}
              
              {/* Ticket Status Badge */}
              {event.ticket_status && event.ticket_status !== 'available' && <div className="absolute top-4 right-4">
                  {event.ticket_status === 'sold_out' ? <Badge className="bg-red-500 text-white border-0 px-4 py-2 text-sm rounded-xl shadow-lg">
                      <AlertCircle className="w-4 h-4 mr-2" />
                      {isRTL ? "نفذت التذاكر" : "Sold Out"}
                    </Badge> : event.ticket_status === 'limited' && <Badge className="bg-orange-500 text-white border-0 px-4 py-2 text-sm rounded-xl shadow-lg">
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      {isRTL ? "تذاكر محدودة" : "Limited"}
                    </Badge>}
                </div>}
            </div>

            {/* Action Buttons on Image */}
            <div className="absolute bottom-4 left-4 right-4 flex gap-3">
              <Button onClick={handleFavorite} variant="secondary" style={{
              boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }} className="flex-1 gap-2 backdrop-blur-sm rounded-xl py-3 bg-indigo-900 hover:bg-indigo-800">
                <Heart className={`w-5 h-5 ${event && isFavorite(event.id) ? "fill-red-500 text-red-500" : ""}`} />
                {isRTL ? "أضف للمفضلة" : "Add to Favorites"}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="secondary" style={{
                  boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                }} className="gap-2 backdrop-blur-sm rounded-xl py-3 px-4 bg-indigo-900 hover:bg-indigo-800">
                    <Share2 className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-white z-50 min-w-[180px] shadow-lg border border-gray-200">
                  <DropdownMenuItem onClick={shareToWhatsApp} className="gap-3 cursor-pointer hover:bg-gray-100">
                    <MessageCircle className="w-4 h-4 text-green-500" />
                    <span>{isRTL ? "واتساب" : "WhatsApp"}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={shareToTwitter} className="gap-3 cursor-pointer hover:bg-gray-100">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                    <span>{isRTL ? "إكس (تويتر)" : "X (Twitter)"}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={shareToFacebook} className="gap-3 cursor-pointer hover:bg-gray-100">
                    <svg className="w-4 h-4 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                    <span>{isRTL ? "فيسبوك" : "Facebook"}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={shareToTelegram} className="gap-3 cursor-pointer hover:bg-gray-100">
                    <Send className="w-4 h-4 text-blue-400" />
                    <span>{isRTL ? "تلجرام" : "Telegram"}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={copyLink} className="gap-3 cursor-pointer hover:bg-gray-100">
                    <ExternalLink className="w-4 h-4 text-gray-500" />
                    <span>{isRTL ? "نسخ الرابط" : "Copy Link"}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </motion.div>

          {/* Details Section */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="space-y-6">
            {/* Source Badge */}
            <Badge variant="secondary" className="text-sm px-4 py-1.5 rounded-lg bg-primary/15 text-primary font-semibold border border-primary/20 shadow-sm">
              {event.source_name}
            </Badge>

            {/* Title */}
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 leading-tight">
              {eventTitle}
            </h1>

            {/* Countdown Timer */}
            {countdown && !countdown.isPast && <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.3
          }} className="rounded-2xl p-5 md:p-6" style={{
            background: 'linear-gradient(145deg, #f5f5f5, #ffffff)',
            boxShadow: 'inset 4px 4px 10px #e0e0e0, inset -4px -4px 10px #ffffff'
          }}>
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-primary" />
                  <span className="text-sm font-semibold text-gray-600">
                    {isRTL ? "العد التنازلي للفعالية" : "Event Countdown"}
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-3 md:gap-4 text-center">
                  {[{
                value: countdown.days,
                label: isRTL ? "يوم" : "Days"
              }, {
                value: countdown.hours,
                label: isRTL ? "ساعة" : "Hours"
              }, {
                value: countdown.minutes,
                label: isRTL ? "دقيقة" : "Minutes"
              }, {
                value: countdown.seconds,
                label: isRTL ? "ثانية" : "Seconds"
              }].map((item, idx) => <div key={idx} className="rounded-xl p-3 md:p-4" style={{
                background: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
                boxShadow: '4px 4px 10px #e0e0e0, -4px -4px 10px #ffffff'
              }}>
                      <div className="text-2xl md:text-3xl font-bold text-primary">
                        {item.value.toString().padStart(2, '0')}
                      </div>
                      <div className="text-xs md:text-sm text-gray-500 font-medium mt-1">
                        {item.label}
                      </div>
                    </div>)}
                </div>
              </motion.div>}

            {/* Event Past Notice */}
            {countdown?.isPast && <div className="rounded-2xl p-4 flex items-center gap-3" style={{
            background: 'linear-gradient(145deg, #fff5f5, #ffffff)',
            boxShadow: '4px 4px 10px #e8e8e8, -4px -4px 10px #ffffff'
          }}>
                <AlertCircle className="w-5 h-5 text-orange-500" />
                <span className="text-gray-600 font-medium">
                  {isRTL ? "انتهت هذه الفعالية" : "This event has ended"}
                </span>
              </div>}

            {/* Event Info Cards */}
            <div className="grid sm:grid-cols-2 gap-4">
              {event.date && <div className="flex items-center gap-4 p-4 rounded-2xl" style={{
              background: 'linear-gradient(145deg, #f8f8f8, #ffffff)',
              boxShadow: '4px 4px 10px #e8e8e8, -4px -4px 10px #ffffff'
            }}>
                  <div className="p-3 rounded-xl bg-primary/10">
                    <Calendar className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{isRTL ? "التاريخ" : "Date"}</p>
                    <p className="font-semibold text-gray-700">{event.date}</p>
                  </div>
                </div>}

              {(eventLocation || hasMapLink) && <div className="flex items-center gap-4 p-4 rounded-2xl" style={{
              background: 'linear-gradient(145deg, #f8f8f8, #ffffff)',
              boxShadow: '4px 4px 10px #e8e8e8, -4px -4px 10px #ffffff'
            }}>
                  <div className="p-3 rounded-xl bg-blue-500/10">
                    <MapPin className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{isRTL ? "الموقع" : "Location"}</p>
                    {eventLocation ? (
                      <p className="font-semibold text-gray-700">{eventLocation}</p>
                    ) : hasMapLink ? (
                      <a 
                        href={rawLocation?.includes('http') ? rawLocation : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(rawLocation || '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-blue-600 hover:text-blue-700 underline flex items-center gap-1"
                      >
                        {isRTL ? "عرض على الخريطة" : "View on Map"}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : null}
                  </div>
                </div>}

              {(event.start_time || event.end_time) && <div className="flex items-center gap-4 p-4 rounded-2xl sm:col-span-2" style={{
              background: 'linear-gradient(145deg, #f8f8f8, #ffffff)',
              boxShadow: '4px 4px 10px #e8e8e8, -4px -4px 10px #ffffff'
            }}>
                  <div className="p-3 rounded-xl bg-purple-500/10">
                    <Clock className="w-6 h-6 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{isRTL ? "الوقت" : "Time"}</p>
                    <p className="font-semibold text-gray-700">
                      {event.start_time && event.start_time}
                      {event.start_time && event.end_time && " - "}
                      {event.end_time && event.end_time}
                    </p>
                  </div>
                </div>}
            </div>

            {/* Description */}
            {eventDescription && <div className="space-y-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {isRTL ? "عن الفعالية" : "About the Event"}
                </h2>
                <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {eventDescription}
                </p>
              </div>}

            {/* CTA Button */}
            {event.external_link && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }}>
                <Button asChild size="lg" className="w-full sm:w-auto gap-3 bg-gradient-to-r from-primary to-primary-400 hover:from-primary/90 hover:to-primary-400/90 text-white rounded-2xl py-6 px-8 text-lg font-semibold" style={{
              boxShadow: '6px 6px 20px rgba(107, 52, 128, 0.3)'
            }}>
                  <a href={event.external_link} target="_blank" rel="noopener noreferrer">
                    {event.category === 'Jobs' 
                      ? <Briefcase className="w-5 h-5" />
                      : event.category === 'Courses'
                        ? <GraduationCap className="w-5 h-5" />
                        : <ExternalLink className="w-5 h-5" />}
                    {event.category === 'Jobs' 
                      ? (isRTL ? "قدم على الوظيفة" : "Apply for Job")
                      : event.category === 'Courses'
                        ? (isRTL ? "سجل في الدورة" : "Register for Course")
                        : (isRTL ? "احجز الآن" : "Book Now")}
                  </a>
                </Button>
              </motion.div>}
          </motion.div>
          
          {/* Sidebar Ad Section - Desktop Only */}
          <div className="hidden lg:block">
            <div className="sticky top-24">
              <SidebarAd maxAds={2} />
            </div>
          </div>
        </div>
        
        {/* Mobile Sidebar Ad */}
        <div className="lg:hidden mt-8">
          <SidebarAd maxAds={1} />
        </div>
        {/* Similar Events Section */}
        {similarEvents.length > 0 && <motion.section initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="mt-12 md:mt-16">
            <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6">
              {isRTL ? "فعاليات مشابهة" : "Similar Events"}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {similarEvents.map(similarEvent => {
            const title = isRTL ? similarEvent.title_ar || similarEvent.title : similarEvent.title;
            const location = isRTL ? similarEvent.location_ar || similarEvent.location : similarEvent.location;
            return <motion.div key={similarEvent.id} whileHover={{
              y: -5
            }} onClick={() => navigate(`/event/${similarEvent.id}`)} className="cursor-pointer rounded-2xl overflow-hidden" style={{
              background: 'linear-gradient(145deg, #f0f0f0, #ffffff)',
              boxShadow: '6px 6px 15px #d1d1d1, -6px -6px 15px #ffffff'
            }}>
                    {/* Image */}
                    <div className="relative h-28 md:h-36 overflow-hidden m-2 rounded-xl">
                      {similarEvent.image_url ? <img src={similarEvent.image_url} alt={title} className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" /> : <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                          <Calendar className="w-8 h-8 text-gray-300" />
                        </div>}
                    </div>
                    
                    {/* Content */}
                    <div className="p-3 bg-white/80">
                      <h3 className="font-semibold text-sm md:text-base text-gray-800 line-clamp-2 mb-2 hover:text-primary transition-colors">
                        {title}
                      </h3>
                      {similarEvent.date && <div className="flex items-center gap-2 text-xs text-gray-700">
                          <Calendar className="w-4 h-4 text-primary" />
                          <span>{similarEvent.date}</span>
                        </div>}
                      {location && <div className="flex items-center gap-2 text-xs text-gray-700 mt-1.5">
                          <MapPin className="w-4 h-4 text-blue-600" />
                          <span className="line-clamp-1">{location}</span>
                        </div>}
                    </div>
                  </motion.div>;
          })}
            </div>
          </motion.section>}
      </main>

      <Footer />
    </div>;
};
export default EventDetails;